import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress,
  Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  Grid, Stack, Switch, Tab, Tabs,
  Typography,
} from '@mui/material';
import {
  AllInclusive, Assessment, Cancel, CheckCircle, Close, ContactSupport,
  Extension, ExpandMore, ExpandLess, History, Inventory2,
  KeyboardArrowUp, Lock, NotificationsActive, QrCodeScanner,
  Security, Star, TrendingUp, Warehouse, Storefront,
} from '@mui/icons-material';
import { tenantApi } from '../../api/tenantApi';
import { useReadOnly } from '../../context/ReadOnlyContext';

// ── Default feature-to-AddOn mapping (used as fallback) ─────────────────────
// These defaults are overridden by the dynamic addonConfig from the backend.

const DEFAULT_ADDON_FEATURES = {
  'inventory-items': [
    { key: 'inventory.batch_operations',           label: 'Batch Operations' },
    { key: 'inventory.low_stock_alerts',            label: 'Low Stock Alerts' },
    { key: 'inventory.fifo_lifo_costing',           label: 'FIFO/LIFO Costing' },
    { key: 'inventory.reorder_point_automation',    label: 'Reorder Point Automation' },
    { key: 'inventory.negative_stock_allow',        label: 'Allow Negative Stock' },
    { key: 'inventory.inventory_reservations',      label: 'Inventory Reservations' },
    { key: 'inventory.unit_of_measure_conversion',  label: 'Unit of Measure Conversion' },
    { key: 'inventory.inventory_forecasting',       label: 'Inventory Forecasting' },
    { key: 'inventory.cycle_count',                 label: 'Cycle Count' },
  ],
  'lot-tracking': [
    { key: 'inventory.lot_number_tracking',    label: 'Lot/Batch Number Tracking' },
    { key: 'inventory.expiry_date_tracking',   label: 'Expiry Date Tracking' },
    { key: 'inventory.serial_number_tracking', label: 'Serial Number Tracking' },
  ],
  'barcode-scanning': [
    { key: 'inventory.barcode_scanning',       label: 'Barcode / QR Scanning' },
    { key: 'integrations.barcode_label_printing', label: 'Barcode Label Printing' },
  ],
  'inventory-bundles': [
    { key: 'inventory.bundle_products',        label: 'Bundle / Kit Products' },
    { key: 'inventory.bundle_assembly',        label: 'Build Bundles' },
  ],
  'warehouses': [
    { key: 'warehousing.multi_warehouse',         label: 'Multi-Warehouse' },
    { key: 'warehousing.bin_location_management', label: 'Bin / Location Management' },
    { key: 'warehousing.transfer_orders',         label: 'Transfer Orders' },
    { key: 'warehousing.receiving_workflows',     label: 'Receiving Workflows' },
    { key: 'warehousing.putaway_rules',           label: 'Putaway Rules' },
    { key: 'warehousing.wave_picking',            label: 'Wave Picking' },
    { key: 'warehousing.cross_docking',           label: 'Cross-Docking' },
    { key: 'warehousing.zone_management',         label: 'Zone Management' },
  ],
  'history-retention': [
    { key: 'reporting.audit_log_export', label: 'Audit Log Export' },
  ],
  'scheduled-reports': [
    { key: 'reporting.scheduled_reports',           label: 'Scheduled Reports' },
    { key: 'reporting.custom_dashboards',           label: 'Custom Dashboards' },
    { key: 'reporting.advanced_analytics',          label: 'Advanced Analytics' },
    { key: 'reporting.inventory_valuation_reports', label: 'Inventory Valuation Reports' },
    { key: 'reporting.cost_analysis',               label: 'Cost Analysis' },
  ],
  'data-exports': [
    { key: 'reporting.export_csv',   label: 'CSV Export' },
    { key: 'reporting.export_excel', label: 'Excel Export' },
    { key: 'reporting.export_pdf',   label: 'PDF Export' },
  ],
  'notifications': [
    { key: 'notifications.email_alerts',        label: 'Email Alerts' },
    { key: 'notifications.sms_alerts',          label: 'SMS Alerts' },
    { key: 'notifications.slack_integration',   label: 'Slack Integration' },
    { key: 'notifications.in_app_notifications', label: 'In-App Notifications' },
    { key: 'notifications.push_notifications',  label: 'Push Notifications' },
    { key: 'notifications.digest_emails',       label: 'Daily/Weekly Digest Emails' },
  ],
  'advanced-audit-trail': [
    { key: 'security.advanced_audit_log',    label: 'Advanced Audit Logging' },
    { key: 'security.data_masking',          label: 'Data Masking' },
    { key: 'security.field_level_encryption', label: 'Field-Level Encryption' },
  ],
  'team-users': [
    { key: 'security.mfa_enforcement',        label: 'Enforce MFA' },
    { key: 'security.ip_allowlisting',        label: 'IP Allowlisting' },
    { key: 'security.sso_saml',               label: 'SSO / SAML' },
    { key: 'security.session_timeout_policy', label: 'Session Timeout Policy' },
    { key: 'licensing.multi_user_access',     label: 'Multi-User Access' },
  ],
  'api-requests': [
    { key: 'api_management.api_key_self_service', label: 'Self-Service API Keys' },
    { key: 'api_management.rate_limit_override',  label: 'Rate Limit Override' },
    { key: 'api_management.graphql_api',           label: 'GraphQL API' },
    { key: 'api_management.webhook_delivery',      label: 'Webhook Delivery' },
    { key: 'api_management.api_usage_analytics',   label: 'API Usage Analytics' },
    { key: 'integrations.webhooks',                label: 'Webhooks' },
    { key: 'integrations.public_api_access',       label: 'Public API Access' },
  ],
};

// Features not mapped to any add-on — shown under "Base Plan" card
const DEFAULT_BASE_PLAN_FEATURES = [
  { key: 'integrations.ecommerce_connectors',   label: 'E-Commerce Connectors' },
  { key: 'integrations.erp_sync',               label: 'ERP Sync' },
  { key: 'integrations.edi_support',            label: 'EDI Support' },
  { key: 'integrations.accounting_integration', label: 'Accounting Integration' },
  { key: 'integrations.shipping_carriers',      label: 'Shipping Carrier Integration' },
  { key: 'licensing.usage_dashboard',           label: 'Usage Dashboard' },
  { key: 'licensing.quota_warnings',            label: 'Quota Warning Notifications' },
  { key: 'licensing.self_service_billing',      label: 'Self-Service Billing' },
  { key: 'licensing.custom_branding',           label: 'Custom Branding' },
];

// Features enabled and included by default with every plan, grouped by category
const DEFAULT_INCLUDED_FEATURES = [
  { category: 'Inventory', features: [
    { key: 'inventory.batch_operations', label: 'Batch Operations' },
    { key: 'inventory.low_stock_alerts', label: 'Low Stock Alerts' },
  ]},
  { category: 'Warehousing', features: [
    { key: 'warehousing.receiving_workflows', label: 'Receiving Workflows' },
  ]},
  { category: 'Reporting', features: [
    { key: 'reporting.export_csv', label: 'CSV Export' },
  ]},
  { category: 'Integrations', features: [
    { key: 'integrations.public_api_access', label: 'Public API Access' },
  ]},
  { category: 'Security', features: [
    { key: 'security.session_timeout_policy', label: 'Session Timeout Policy' },
  ]},
  { category: 'Notifications', features: [
    { key: 'notifications.email_alerts', label: 'Email Alerts' },
    { key: 'notifications.in_app_notifications', label: 'In-App Notifications' },
  ]},
  { category: 'API Management', features: [
    { key: 'api_management.api_key_self_service', label: 'Self-Service API Keys' },
    { key: 'api_management.api_usage_analytics', label: 'API Usage Analytics' },
  ]},
  { category: 'Licensing', features: [
    { key: 'licensing.usage_dashboard', label: 'Usage Dashboard' },
    { key: 'licensing.quota_warnings', label: 'Quota Warning Notifications' },
    { key: 'licensing.multi_user_access', label: 'Multi-User Access' },
  ]},
];

// Default category mapping (used as fallback)
const DEFAULT_ADDON_CATEGORIES = {
  'base-plan':           'Platform',
  'inventory-items':     'Inventory',
  'lot-tracking':        'Inventory',
  'barcode-scanning':    'Inventory',
  'inventory-bundles':   'Inventory',
  'warehouses':          'Warehousing',
  'history-retention':   'Reporting',
  'scheduled-reports':   'Reporting',
  'data-exports':        'Reporting',
  'notifications':       'Notifications',
  'advanced-audit-trail': 'Security',
  'team-users':          'Security',
  'api-requests':        'API & Integrations',
};

const CATEGORY_ORDER = ['All', 'Inventory', 'Warehousing', 'Reporting', 'Notifications', 'Security', 'API & Integrations', 'Platform'];

// ── Build effective feature-to-addon mapping from backend config ─────────────
function buildEffectiveMapping(addonConfig) {
  // Start with default mapping: { featureKey: addonId }
  const mapping = {};
  for (const [addonId, features] of Object.entries(DEFAULT_ADDON_FEATURES)) {
    for (const f of features) {
      mapping[f.key] = addonId;
    }
  }
  for (const f of DEFAULT_BASE_PLAN_FEATURES) {
    mapping[f.key] = 'base-plan';
  }
  // Apply overrides from backend
  if (addonConfig?.featureMapping) {
    for (const [featureKey, addonId] of Object.entries(addonConfig.featureMapping)) {
      mapping[featureKey] = addonId;
    }
  }
  return mapping;
}

function buildAddonFeatures(effectiveMapping, allFeatureLabels) {
  // Group features by addon
  const result = {};
  for (const [featureKey, addonId] of Object.entries(effectiveMapping)) {
    if (!result[addonId]) { result[addonId] = []; }
    const label = allFeatureLabels[featureKey] || featureKey.split('.').pop().replace(/_/g, ' ');
    result[addonId].push({ key: featureKey, label });
  }
  return result;
}

// Build a flat label map from DEFAULT arrays
const ALL_FEATURE_LABELS = {};
for (const features of Object.values(DEFAULT_ADDON_FEATURES)) {
  for (const f of features) { ALL_FEATURE_LABELS[f.key] = f.label; }
}
for (const f of DEFAULT_BASE_PLAN_FEATURES) { ALL_FEATURE_LABELS[f.key] = f.label; }

// ── Icon map keyed by featureId ───────────────────────────────────────────────
const FEATURE_ICONS = {
  'base-plan':           <Storefront          sx={{ fontSize: 32, color: 'success.main'   }} />,
  'inventory-items':     <Inventory2          sx={{ fontSize: 32, color: 'primary.main'   }} />,
  'team-users':          <Extension           sx={{ fontSize: 32, color: 'secondary.main' }} />,
  'api-requests':        <TrendingUp          sx={{ fontSize: 32, color: 'info.main'      }} />,
  'warehouses':          <Warehouse           sx={{ fontSize: 32, color: 'success.main'   }} />,
  'history-retention':   <History             sx={{ fontSize: 32, color: 'warning.main'   }} />,
  'lot-tracking':        <Inventory2          sx={{ fontSize: 32, color: 'primary.dark'   }} />,
  'barcode-scanning':    <QrCodeScanner       sx={{ fontSize: 32, color: 'success.dark'   }} />,
  'inventory-bundles':   <Extension           sx={{ fontSize: 32, color: 'primary.main'   }} />,
  'scheduled-reports':   <Assessment          sx={{ fontSize: 32, color: 'info.dark'      }} />,
  'notifications':       <NotificationsActive sx={{ fontSize: 32, color: 'warning.dark'   }} />,
  'advanced-audit-trail': <Security           sx={{ fontSize: 32, color: 'error.main'     }} />,
  'data-exports':        <AllInclusive        sx={{ fontSize: 32, color: 'secondary.dark' }} />,
};

const LEVEL_COLORS = {
  default:      'default',
  standard:     'primary',
  professional: 'secondary',
  custom:       'warning',
};

// NOTE: the `default` level is presented to users as "Basic" everywhere.
// The internal level key stays `default` (it's the persisted value + the
// fallback when a feature has no explicit level).
const LEVEL_CHIP_LABELS = {
  default:      'Basic',
  standard:     'Standard',
  professional: 'Professional',
  custom:       'Custom',
};

function formatLimit(limit, unit) {
  if (limit === null) { return 'Unlimited'; }
  if (limit === 0) { return 'Not included'; }
  return `${limit.toLocaleString()} ${unit}`;
}

function formatPrice(price, limit) {
  if (price === null) { return 'Contact Sales'; }
  if (price === 0 && limit === 0) { return 'Upgrade to enable'; }
  if (price === 0) { return 'Included'; }
  return `+$${price.toFixed(2)}/mo`;
}

// ── Feature list inside a card (single-line rows with toggle switches) ──────
function FeatureList({ features, featureFlags, onToggleFeature, readOnly, basePlanFeatures, locked }) {
  if (!features || features.length === 0) { return null; }
  const basePlanSet = basePlanFeatures instanceof Set ? basePlanFeatures : new Set(basePlanFeatures || []);

  // Basic Inventory features first, then alphabetical by label within each group.
  const sortedFeatures = [...features].sort((a, b) => {
    const aBase = basePlanSet.has(a.key);
    const bBase = basePlanSet.has(b.key);
    if (aBase !== bBase) { return aBase ? -1 : 1; }
    return a.label.localeCompare(b.label);
  });

  return (
    <Box sx={{ mt: 1 }}>
      {sortedFeatures.map(({ key, label }) => {
        const isBasePlan = basePlanSet.has(key);
        // Base-plan features are always included (shown on) and can't be toggled —
        // the switch stays visible but greyed out. When the add-on is locked
        // (not active), every feature is also non-editable.
        const enabled = isBasePlan ? true : (!locked && featureFlags[key] !== false);
        const toggleDisabled = readOnly || locked || isBasePlan;
        // Icon: green check when on; when off, a red X circle if the user CAN
        // turn it on, otherwise a lock (disabled/locked, not user-changeable).
        const changeable = Boolean(onToggleFeature) && !toggleDisabled;
        return (
          <Stack
            key={key}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ py: 0.25, minHeight: 32 }}
          >
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
              {enabled
                ? <CheckCircle sx={{ fontSize: 14, color: 'success.main', flexShrink: 0 }} />
                : (changeable
                  ? <Cancel sx={{ fontSize: 14, color: 'error.main', flexShrink: 0 }} />
                  : <Lock sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />)}
              <Typography variant="caption" noWrap sx={{ opacity: enabled ? 1 : 0.6 }}>
                {label}
              </Typography>
              {isBasePlan && (
                <Chip label="Basic" size="small" icon={<Star sx={{ fontSize: '10px !important' }} />}
                  sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600 }} />
              )}
            </Stack>
            {onToggleFeature && (
              <Switch
                size="small"
                checked={enabled}
                onChange={() => { if (!isBasePlan) { onToggleFeature(key, !enabled); } }}
                disabled={toggleDisabled}
                sx={{ ml: 0.5 }}
              />
            )}
          </Stack>
        );
      })}
    </Box>
  );
}

// ── Levels section — single-line rows (matches admin Feature Limits cards) ────
function LevelSelector({ feature, onSelect, readOnly }) {
  const { currentLevel, levels, canUpgrade, unit } = feature;

  return (
    <Stack spacing={0.5}>
      {levels.map(({ level, limit, price }) => {
          const isActive  = level === currentLevel;
          const isCustom  = level === 'custom';
          const canSelect = canUpgrade && !isCustom && !readOnly && !isActive;
          const color     = LEVEL_COLORS[level];
          const colored   = isActive && color !== 'default';

          return (
            <Stack
              key={level}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              onClick={(e) => { e.stopPropagation(); if (canSelect) { onSelect(feature.featureId, level); } }}
              sx={{
                px: 1, py: 0.4, borderRadius: 1,
                bgcolor: colored ? `${color}.50` : 'action.hover',
                border: isActive ? 2 : 0,
                borderColor: colored ? `${color}.main` : 'transparent',
                cursor: canSelect ? 'pointer' : 'default',
                '&:hover': canSelect ? { bgcolor: 'action.selected' } : {},
              }}
            >
              <Chip
                label={LEVEL_CHIP_LABELS[level] || level}
                size="small"
                color={colored ? color : 'default'}
                icon={
                  isActive && !isCustom ? <CheckCircle sx={{ fontSize: '12px !important' }} />
                    : isCustom ? <ContactSupport sx={{ fontSize: '12px !important' }} />
                      : undefined
                }
                sx={{ fontWeight: 600, fontSize: '0.65rem', height: 20, width: 104 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1, textAlign: 'center' }}>
                {formatLimit(limit, unit)}
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                color={colored ? `${color}.main` : 'text.secondary'}
                sx={{ minWidth: 76, textAlign: 'right' }}
              >
                {isCustom ? 'Contact Sales' : formatPrice(price, limit)}
              </Typography>
            </Stack>
          );
        })}
    </Stack>
  );
}

// ── Feature card (add-on) ────────────────────────────────────────────────────
function FeatureCard({ feature, onSelect, onDetail, featureFlags, addonFeatures, readOnly, onToggleFeature, basePlanFeatures, discountPercent, discountAppliesToAddons }) {
  const { featureId, label, description, currentLevel, currentLimit, currentPrice, unit } = feature;
  const [levelsOpen, setLevelsOpen] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const isDefault = currentLevel === 'default';
  const color = LEVEL_COLORS[currentLevel] || 'default';
  const relatedFeatures = addonFeatures[featureId] || [];
  const showDiscount = discountPercent > 0 && discountAppliesToAddons;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderColor: !isDefault ? `${color}.main` : 'divider',
        borderWidth: !isDefault ? 2 : 1,
        transition: 'border-color 0.2s',
      }}
    >
      <CardContent sx={{ pb: 1, cursor: 'pointer' }} onClick={() => onDetail(feature)}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
          {FEATURE_ICONS[featureId] || <Extension sx={{ fontSize: 32 }} />}
          <Stack direction="row" spacing={0.5} alignItems="center">
            {showDiscount && (
              <Chip
                label={`${discountPercent}% off`}
                size="small"
                color="success"
                sx={{ fontWeight: 600, fontSize: '0.65rem', height: 20 }}
              />
            )}
            <Chip
              label={LEVEL_CHIP_LABELS[currentLevel] || currentLevel}
              size="small"
              color={color !== 'default' ? color : 'default'}
              icon={<Star sx={{ fontSize: '0.75rem !important' }} />}
              sx={{ fontWeight: 700, fontSize: '0.7rem' }}
            />
          </Stack>
        </Stack>

        <Typography variant="subtitle1" fontWeight={700} mb={0.25}>{label}</Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          {description}
        </Typography>

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color="text.secondary">
            Limit: <strong>{formatLimit(currentLimit, unit)}</strong>
          </Typography>
          <Typography variant="caption" fontWeight={700} color={isDefault ? 'text.secondary' : `${color}.main`}>
            {formatPrice(currentPrice, currentLimit)}
          </Typography>
        </Stack>
      </CardContent>

      {/* LEVELS — collapsible, just under the limits section */}
      <Divider />
      <Box
        sx={{ px: 2, py: 0.5, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        onClick={() => setLevelsOpen((o) => !o)}
      >
        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Levels
        </Typography>
        {levelsOpen ? <ExpandLess sx={{ fontSize: 18, color: 'text.secondary' }} /> : <ExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} />}
      </Box>
      <Collapse in={levelsOpen}>
        <Box sx={{ px: 2, pb: 1 }}>
          <LevelSelector feature={feature} onSelect={onSelect} readOnly={readOnly} />
        </Box>
      </Collapse>

      {/* FEATURES — collapsible */}
      {relatedFeatures.length > 0 && (
        <>
          <Divider />
          <Box
            sx={{ px: 2, py: 0.5, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            onClick={() => setFeaturesOpen((o) => !o)}
          >
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Features
            </Typography>
            {featuresOpen ? <ExpandLess sx={{ fontSize: 18, color: 'text.secondary' }} /> : <ExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} />}
          </Box>
          <Collapse in={featuresOpen}>
            <Box sx={{ px: 2, pb: 1 }}>
              <FeatureList
                features={relatedFeatures}
                featureFlags={featureFlags}
                onToggleFeature={onToggleFeature}
                readOnly={readOnly}
                basePlanFeatures={basePlanFeatures}
                locked={isDefault}
              />
            </Box>
          </Collapse>
        </>
      )}
    </Card>
  );
}

// ── Categorized feature list (one per line, bold category headings) ──────────
function CategorizedFeatureList({ groups, featureFlags }) {
  return (
    <Box sx={{ mt: 0.5 }}>
      {[...groups].sort((a, b) => a.category.localeCompare(b.category)).map(({ category, features }) => (
        <Box key={category} sx={{ mb: 1 }}>
          <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.25 }}>
            {category}
          </Typography>
          {[...features].sort((a, b) => a.label.localeCompare(b.label)).map(({ key, label }) => {
            const enabled = featureFlags[key] !== false;
            return (
              <Stack key={key} direction="row" spacing={0.75} alignItems="center" sx={{ pl: 1, py: 0.15 }}>
                {enabled
                  ? <CheckCircle sx={{ fontSize: 14, color: 'success.main' }} />
                  : <Lock sx={{ fontSize: 14, color: 'text.disabled' }} />}
                <Typography variant="caption" sx={{ opacity: enabled ? 1 : 0.6 }}>
                  {label}
                </Typography>
              </Stack>
            );
          })}
        </Box>
      ))}
    </Box>
  );
}

// ── Basic Inventory card (pseudo add-on showing plan status) ─────────────────
function BasicInventoryCard({ featureFlags, planStatus, disabledFeatures, basePlanFeatures, basePlanPrice, discountPercent }) {
  const [expanded, setExpanded] = useState(false);

  // Build feature groups from basePlanFeatures (dynamic from backend) or fall back to DEFAULT_INCLUDED_FEATURES
  const featureGroups = (() => {
    if (basePlanFeatures && basePlanFeatures.length > 0) {
      // Group basePlanFeatures by category prefix, filtering out disabled features
      const groups = {};
      const categoryMap = {
        inventory: 'Inventory', warehousing: 'Warehousing', reporting: 'Reporting',
        integrations: 'Integrations', security: 'Security', notifications: 'Notifications',
        api_management: 'API Management', licensing: 'Licensing',
      };
      for (const key of basePlanFeatures) {
        if (disabledFeatures.has(key)) { continue; }
        const prefix = key.split('.')[0];
        const cat = categoryMap[prefix] || 'Other';
        if (!groups[cat]) { groups[cat] = []; }
        const label = ALL_FEATURE_LABELS[key] || key.split('.').pop().replace(/_/g, ' ');
        groups[cat].push({ key, label });
      }
      return Object.entries(groups).map(([category, features]) => ({ category, features }));
    }
    // Fallback to hardcoded defaults
    return DEFAULT_INCLUDED_FEATURES
      .map(({ category, features }) => ({
        category,
        features: features.filter((f) => !disabledFeatures.has(f.key)),
      }))
      .filter((g) => g.features.length > 0);
  })();

  const totalIncluded = featureGroups.reduce((sum, g) => sum + g.features.length, 0);

  // Determine status chip props
  const statusConfig = {
    trial:  { label: 'Trial',  color: 'warning' },
    paid:   { label: 'Paid',   color: 'success' },
    locked: { label: 'Locked', color: 'error'   },
  };
  const { label: statusLabel, color: statusColor } = statusConfig[planStatus] || statusConfig.trial;

  const borderColor = planStatus === 'locked' ? 'error.main' : planStatus === 'paid' ? 'success.main' : 'warning.main';

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderColor,
        borderWidth: 2,
      }}
    >
      <CardContent sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
          {FEATURE_ICONS['base-plan']}
          <Chip
            label={statusLabel}
            size="small"
            color={statusColor}
            sx={{ fontWeight: 700, fontSize: '0.7rem' }}
          />
        </Stack>

        <Typography variant="subtitle1" fontWeight={700} mb={0.25}>Basic Inventory</Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          Platform essentials included with every plan.
        </Typography>

        <Stack direction="row" spacing={0.75} alignItems="baseline">
          <Typography variant="h6" fontWeight={800} color="primary.main">
            ${(basePlanPrice ?? 0).toFixed(2)}
          </Typography>
          <Typography variant="caption" color="text.secondary">/mo</Typography>
          {discountPercent > 0 && (
            <Chip
              label={`${discountPercent}% off`}
              size="small"
              color="success"
              sx={{ fontWeight: 600, fontSize: '0.65rem', height: 18 }}
            />
          )}
        </Stack>
      </CardContent>

      <Divider />
      <Box
        sx={{ px: 2, py: 0.5, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        onClick={() => setExpanded((e) => !e)}
      >
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Features
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {totalIncluded} included
          </Typography>
        </Stack>
        {expanded ? <ExpandLess sx={{ fontSize: 18, color: 'text.secondary' }} /> : <ExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} />}
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 1.5 }}>
          <CategorizedFeatureList groups={featureGroups} featureFlags={featureFlags} />
        </Box>
      </Collapse>
    </Card>
  );
}

// ── Feature detail modal ──────────────────────────────────────────────────────
function FeatureModal({ feature, open, onClose, saving, onSelect, salesEmail, featureFlags, addonFeatures, readOnly, onToggleFeature, basePlanFeatures }) {
  if (!feature) { return null; }
  const { featureId, label, description, unit, levels, currentLevel, canUpgrade } = feature;
  const relatedFeatures = addonFeatures[featureId] || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {FEATURE_ICONS[featureId]}
          <Box flex={1}>
            <Typography variant="h6" fontWeight={700}>{label}</Typography>
            <Typography variant="caption" color="text.secondary">{description}</Typography>
          </Box>
          <Button onClick={onClose} size="small" sx={{ minWidth: 0, p: 0.5 }}>
            <Close />
          </Button>
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {!canUpgrade && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            Upgrade to a paid plan to change feature levels.
          </Alert>
        )}

        <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1}>
          SELECT LEVEL
        </Typography>

        <Stack spacing={1.5}>
          {levels.map(({ level, limit, price, label: lvlLabel }) => {
            const isActive  = level === currentLevel;
            const isCustom  = level === 'custom';
            const canSelect = canUpgrade && !isCustom && !saving && !readOnly;
            const levelColor = LEVEL_COLORS[level];

            return (
              <Box
                key={level}
                onClick={() => canSelect && level !== currentLevel && onSelect(featureId, level)}
                sx={{
                  border: 2,
                  borderColor: isActive ? `${levelColor}.main` : 'divider',
                  borderRadius: 2,
                  px: 2, py: 1.5,
                  bgcolor: isActive ? `${levelColor}.50` : 'background.paper',
                  cursor: canSelect && !isActive ? 'pointer' : 'default',
                  transition: 'all 0.15s',
                  '&:hover': canSelect && !isActive ? { borderColor: `${levelColor}.light`, bgcolor: 'action.hover' } : {},
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                    {isActive && <CheckCircle sx={{ fontSize: 18, color: `${levelColor}.main` }} />}
                    {!isActive && isCustom && <ContactSupport sx={{ fontSize: 18, color: 'text.secondary' }} />}
                    {!isActive && !isCustom && <KeyboardArrowUp sx={{ fontSize: 18, color: 'text.disabled', opacity: isActive ? 0 : 1 }} />}
                    <Box>
                      <Typography variant="body2" fontWeight={isActive ? 700 : 500}>
                        {lvlLabel}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatLimit(limit, unit)}
                      </Typography>
                    </Box>
                  </Stack>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    color={isActive ? `${levelColor}.main` : 'text.secondary'}
                  >
                    {isCustom ? 'Contact Sales' : formatPrice(price, limit)}
                  </Typography>
                </Stack>
              </Box>
            );
          })}
        </Stack>

        {/* Related features in the modal */}
        {relatedFeatures.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1}>
              FEATURES INCLUDED WITH THIS ADD-ON
            </Typography>
            <FeatureList
              features={relatedFeatures}
              featureFlags={featureFlags}
              onToggleFeature={onToggleFeature}
              readOnly={readOnly}
              basePlanFeatures={basePlanFeatures}
              locked={currentLevel === 'default'}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 2 }}>
        <Button onClick={onClose}>Close</Button>
        {feature.levels.find((l) => l.level === 'custom') && (
          <Button
            variant="outlined"
            startIcon={<ContactSupport />}
            href={`mailto:${salesEmail || 'sales@example.com'}`}
            target="_blank"
          >
            Contact Sales
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function AddOnsPage() {
  const { isReadOnly } = useReadOnly();
  const [data,         setData]        = useState(null);
  const [featureFlags, setFeatureFlags] = useState({});
  const [loading,      setLoading]     = useState(true);
  const [error,        setError]       = useState(null);
  const [selected,     setSelected]    = useState(null);
  const [saving,       setSaving]      = useState(false);
  const [toast,        setToast]       = useState(null);
  const [categoryTab,  setCategoryTab] = useState('All');

  // Build dynamic feature-to-addon mapping from backend config
  const addonConfigData = data?.addonConfig || {};
  const effectiveMapping = buildEffectiveMapping(addonConfigData);
  const disabledFeaturesSet = new Set(addonConfigData.disabledFeatures || []);
  const addonFeatures = buildAddonFeatures(effectiveMapping, ALL_FEATURE_LABELS);
  // Filter out platform-disabled features from each addon's feature list
  for (const addonId of Object.keys(addonFeatures)) {
    addonFeatures[addonId] = addonFeatures[addonId].filter((f) => !disabledFeaturesSet.has(f.key));
  }
  // Merge custom addon categories into the category mapping
  const addonCategories = { ...DEFAULT_ADDON_CATEGORIES };
  for (const customAddon of (addonConfigData.customAddons || [])) {
    addonCategories[customAddon.id] = customAddon.category || 'Platform';
  }

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [addonsRes, featuresRes] = await Promise.all([
        tenantApi.getAddOns(),
        tenantApi.getMyFeatures(),
      ]);
      setData(addonsRes);
      setFeatureFlags(featuresRes || {});
    } catch {
      setError('Failed to load features. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSelect = async (featureId, level) => {
    setSaving(true);
    try {
      await tenantApi.updateAddOn(featureId, { level });
      setToast({ severity: 'success', message: `${featureId} updated to ${level}.` });
      setSelected(null);
      await load();
    } catch (e) {
      setToast({ severity: 'error', message: e.message || 'Failed to update feature level.' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleFeature = async (featureKey, enabled) => {
    // Optimistic update
    setFeatureFlags((prev) => ({ ...prev, [featureKey]: enabled }));
    try {
      await tenantApi.updateMyFeature(featureKey, enabled);
    } catch (e) {
      // Revert on failure
      setFeatureFlags((prev) => ({ ...prev, [featureKey]: !enabled }));
      setToast({ severity: 'error', message: e.message || 'Failed to toggle feature.' });
    }
  };

  const isTrial = data?.isTrial;
  const plan = data?.plan || '';
  const tenantStatus = data?.tenantStatus || 'active';
  const planStatus = tenantStatus === 'trial_expired' ? 'locked' : isTrial ? 'trial' : 'paid';

  // Filter add-ons by category tab
  const filteredFeatures = (data?.features || [])
    .filter((f) => {
      if (categoryTab === 'All') { return true; }
      return addonCategories[f.featureId] === categoryTab;
    })
    // Alphabetical by name (the Basic Inventory card is rendered first, before
    // these add-on cards).
    .sort((a, b) => (a.label || '').localeCompare(b.label || ''));

  // Include custom display-only add-ons
  const customDisplayAddons = (addonConfigData.customAddons || []).filter((a) => a.type === 'display');
  const filteredCustomAddons = customDisplayAddons.filter((a) => {
    if (categoryTab === 'All') { return true; }
    return (a.category || 'Platform') === categoryTab;
  });

  const showBasePlan = categoryTab === 'All' || categoryTab === 'Platform';
  // Effective base-plan feature keys: use the admin-configured list when present,
  // otherwise fall back to the same DEFAULT_INCLUDED_FEATURES the Basic Inventory
  // card uses — so base-plan features are locked on the add-on cards either way.
  const basePlanFeaturesArray = (addonConfigData.basePlanFeatures && addonConfigData.basePlanFeatures.length > 0)
    ? addonConfigData.basePlanFeatures
    : DEFAULT_INCLUDED_FEATURES.flatMap((g) => g.features.map((f) => f.key));
  const basePlanFeaturesSet = new Set(basePlanFeaturesArray);

  return (
    <Box maxWidth={1200} mx="auto" px={3} py={4}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" mb={1}>
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Extension sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={700}>Features & Add-Ons</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Manage feature levels and toggle individual capabilities on each add-on card.
          </Typography>
        </Box>
        {plan && (
          <Stack direction="row" spacing={1.5} alignItems="flex-end">
            <Button size="small" variant="outlined" onClick={load} disabled={loading}>
              Reload
            </Button>
            <Stack spacing={0.5} alignItems="flex-end">
              <Chip
                label={isTrial ? 'TRIAL' : 'PAID PLAN'}
                color={isTrial ? 'warning' : 'success'}
                size="small"
                sx={{ fontWeight: 700 }}
              />
              {!isTrial && data && (
                <Typography variant="caption" color="text.secondary">
                  ${data.monthlyTotal?.toFixed(2)}/mo total
                </Typography>
              )}
            </Stack>
          </Stack>
        )}
      </Stack>

      {/* Category tabs */}
      <Box sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={categoryTab}
          onChange={(_, v) => setCategoryTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' } }}
        >
          {CATEGORY_ORDER.map((cat) => (
            <Tab key={cat} label={cat} value={cat} />
          ))}
        </Tabs>
      </Box>

      {/* Trial banner */}
      {isTrial && data?.trialEndsAt && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          You&apos;re on a free trial — all features are available at default limits.
          Upgrade to a paid plan to unlock higher levels.
          {data.trialEndsAt && ` Trial ends ${new Date(data.trialEndsAt).toLocaleDateString()}.`}
        </Alert>
      )}

      {/* Pricing summary */}
      {!isTrial && data && (
        <Box sx={{ bgcolor: 'primary.50', border: 1, borderColor: 'primary.200', borderRadius: 2, px: 3, py: 2, mb: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2" fontWeight={700}>Monthly Bill Estimate</Typography>
                {data.discountPercent > 0 && (
                  <Chip label={`${data.discountPercent}% discount applied`} size="small" color="success" sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Basic Inventory ${data.basePlanPrice?.toFixed(2)} + add-ons ${data.addonTotal?.toFixed(2)}
                {data.discountPercent > 0 && data.discountAppliesToAddons && ' (discount applied to all)'}
                {data.discountPercent > 0 && !data.discountAppliesToAddons && ' (discount on base plan only)'}
              </Typography>
            </Box>
            <Typography variant="h5" fontWeight={800} color="primary.main">
              ${data.monthlyTotal?.toFixed(2)}/mo
            </Typography>
          </Stack>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
      {toast && (
        <Alert severity={toast.severity} sx={{ mb: 3, borderRadius: 2 }} onClose={() => setToast(null)}>
          {toast.message}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : (
        <Grid container spacing={3}>
          {/* Basic Inventory card */}
          {showBasePlan && (
            <Grid item xs={12} sm={6} md={4}>
              <BasicInventoryCard featureFlags={featureFlags} planStatus={planStatus} disabledFeatures={disabledFeaturesSet} basePlanFeatures={addonConfigData.basePlanFeatures} basePlanPrice={data?.basePlanListPrice ?? data?.basePlanPrice} discountPercent={data?.discountPercent} />
            </Grid>
          )}

          {/* Add-on cards */}
          {filteredFeatures.map((feature) => (
            <Grid item xs={12} sm={6} md={4} key={feature.featureId}>
              <FeatureCard
                feature={feature}
                onSelect={handleSelect}
                onDetail={setSelected}
                featureFlags={featureFlags}
                addonFeatures={addonFeatures}
                readOnly={isReadOnly}
                onToggleFeature={handleToggleFeature}
                basePlanFeatures={basePlanFeaturesSet}
                discountPercent={data?.discountPercent}
                discountAppliesToAddons={data?.discountAppliesToAddons}
              />
            </Grid>
          ))}

          {/* Custom display-only add-on cards */}
          {filteredCustomAddons.map((customAddon) => {
            const customFeatures = addonFeatures[customAddon.id] || [];
            const enabledCount = customFeatures.filter(({ key }) => featureFlags[key] !== false).length;
            return (
              <Grid item xs={12} sm={6} md={4} key={customAddon.id}>
                <Card variant="outlined" sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ pb: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                      <Extension sx={{ fontSize: 32, color: 'info.main' }} />
                      <Chip label={customAddon.category || 'Custom'} size="small" variant="outlined" sx={{ fontWeight: 700, fontSize: '0.7rem' }} />
                    </Stack>
                    <Typography variant="subtitle1" fontWeight={700} mb={0.25}>{customAddon.label}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                      {customAddon.description || ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <strong>{enabledCount}</strong> of {customFeatures.length} features enabled
                    </Typography>
                  </CardContent>
                  {customFeatures.length > 0 && (
                    <>
                      <Divider />
                      <Box sx={{ px: 2, pb: 1, pt: 0.5 }}>
                        <FeatureList
                          features={customFeatures}
                          featureFlags={featureFlags}
                          onToggleFeature={handleToggleFeature}
                          readOnly={isReadOnly}
                          basePlanFeatures={basePlanFeaturesSet}
                        />
                      </Box>
                    </>
                  )}
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <FeatureModal
        feature={selected}
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        saving={saving}
        onSelect={handleSelect}
        salesEmail={data?.salesEmail}
        featureFlags={featureFlags}
        addonFeatures={addonFeatures}
        readOnly={isReadOnly}
        onToggleFeature={handleToggleFeature}
        basePlanFeatures={basePlanFeaturesSet}
      />

      {!loading && (
        <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={4}>
          Changes take effect immediately. Billing adjusts on your next invoice.
          Custom levels require a sales conversation — contact us for volume pricing.
        </Typography>
      )}
    </Box>
  );
}
